import React from 'react';
import { cn } from '@/lib/utils';
import { kitFocus, kitPress } from './styles';

type KitButtonSize = 28 | 32;

export interface PartnrIconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Accessible name; also used as the native tooltip unless `title` is set. */
  label: string;
  size?: KitButtonSize;
  /** `solid` is the ink fill used by the send button. */
  tone?: 'ghost' | 'solid';
}

/** Round icon-only button, 28 or 32px. */
export const PartnrIconButton = React.forwardRef<HTMLButtonElement, PartnrIconButtonProps>(
  ({ label, size = 28, tone = 'ghost', className, title, type = 'button', children, ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={title ?? label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full disabled:pointer-events-none disabled:opacity-40',
        size === 28 ? 'h-7 w-7 [&_svg]:h-3.5 [&_svg]:w-3.5' : 'h-8 w-8 [&_svg]:h-4 [&_svg]:w-4',
        tone === 'solid'
          ? 'bg-ink text-paper-raised hover:bg-ink/90'
          : 'text-ink-3 hover:bg-wash hover:text-ink',
        kitPress,
        kitFocus,
        className,
      )}
      {...props}
    >
      {children}
    </button>
  ),
);
PartnrIconButton.displayName = 'PartnrIconButton';

export interface PartnrPillButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: KitButtonSize;
  /** `active` marks an on state (signal text on the signal wash). */
  active?: boolean;
  /** Optional leading icon. */
  icon?: React.ReactNode;
}

/** Pill button with an optional leading icon, 28 or 32px tall. */
export const PartnrPillButton = React.forwardRef<HTMLButtonElement, PartnrPillButtonProps>(
  ({ size = 28, active = false, icon, className, type = 'button', children, ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full font-medium disabled:pointer-events-none disabled:opacity-40',
        size === 28 ? 'h-7 px-2.5 text-[12px] [&_svg]:h-3.5 [&_svg]:w-3.5' : 'h-8 px-3 text-[13px] [&_svg]:h-4 [&_svg]:w-4',
        active ? 'bg-signal-wash text-signal' : 'text-ink-3 hover:bg-wash hover:text-ink',
        kitPress,
        kitFocus,
        className,
      )}
      {...props}
    >
      {icon}
      {children}
    </button>
  ),
);
PartnrPillButton.displayName = 'PartnrPillButton';
